/**
 * useLifestyleAnalytics.js
 *
 * Analytics hook for the Assessment Wizard's Screen 4 ("Lifestyle").
 * Mirrors useGoalAnalytics.js exactly — same GA4 wrapper via react-ga4,
 * same IS_PRODUCTION guard, same dev console.group/table logging.
 *
 * Privacy (Nutrition Policy v1.0 §19 Privacy & Data Minimization): raw
 * health-adjacent answers must never be sent to ordinary analytics
 * events. Stress level, smoking, alcohol and water intake are treated as
 * sensitive here — trackFieldSelect omits their values (interaction-only
 * event) and trackStepNext sends coarse derived booleans instead of the
 * raw values. Occupation, work type, and wake/sleep time are logistics
 * data, not health answers, and are sent as-is.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";
import { HIGH_STRESS_THRESHOLD } from "./LifestyleStep.constants";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[LifestyleAnalytics] ${eventName}`, "color:#00695C;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

// Fields whose *value* is a health-adjacent answer (Nutrition Policy
// §19) — the fact the user interacted with the field is still useful for
// funnel analytics, but the raw answer is not sent.
const SENSITIVE_FIELDS = ["stressLevel", "smoking", "alcohol", "waterIntake"];

const useLifestyleAnalytics = () => {
  const trackStepView = useCallback((sessionId) => {
    send("lifestyle_step_view", { session_id: sessionId, step: 4 });
  }, []);

  // Generic field-level tracker — Lifestyle has 7 distinct fields (vs.
  // Goal's single choice), so one parameterized tracker replaces what
  // would otherwise be 7 near-identical trackXSelect functions.
  const trackFieldSelect = useCallback((sessionId, field, value) => {
    if (SENSITIVE_FIELDS.includes(field)) {
      // Interaction only — never the raw stress/smoking/alcohol/water value.
      send("lifestyle_field_select", { session_id: sessionId, field });
      return;
    }
    send("lifestyle_field_select", {
      session_id: sessionId,
      field,
      value,
    });
  }, []);

  const trackStepNext = useCallback((sessionId, data = {}) => {
    send("lifestyle_step_next", {
      session_id: sessionId,
      step: 4,
      work_type: data.workType,
      // Coarse derived booleans instead of raw stress_level/smoking/
      // alcohol/water_intake_l — see SENSITIVE_FIELDS above.
      high_stress: data.stressLevel != null ? data.stressLevel >= HIGH_STRESS_THRESHOLD : undefined,
      wellness_flag: data.smoking === "yes" || data.alcohol === "yes",
    });
  }, []);

  const trackStepPrevious = useCallback((sessionId) => {
    send("lifestyle_step_previous", { session_id: sessionId, step: 4 });
  }, []);

  const trackValidationBlocked = useCallback((sessionId, errors = {}) => {
    send("lifestyle_validation_blocked", {
      session_id: sessionId,
      missing_fields: Object.keys(errors).join(","),
    });
  }, []);

  return {
    trackStepView,
    trackFieldSelect,
    trackStepNext,
    trackStepPrevious,
    trackValidationBlocked,
  };
};

export default useLifestyleAnalytics;